
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateResponse} from './ConfigUpdateResponse';
import {FieldModelConfigUpdateResponse} from './ConfigUpdateResponse';
import {Currency} from '../core/Currency';
import {FieldModelCurrency} from '../core/Currency';

/**
 * CurrencyDeleteResponse struct
 */
class CurrencyDeleteResponse extends ConfigUpdateResponse {
  /**
   * Initialize struct
   * @param {!ConfigUpdateResponse=} parent
   * @param {!Currency=} currency
   * @constructor
   */
  constructor (parent = new ConfigUpdateResponse(), currency = new Currency()) {
    super()
    super.copy(parent)
    this.Currency = currency
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CurrencyDeleteResponse}
   * @param {!CurrencyDeleteResponse} other Other struct
   * @returns {!CurrencyDeleteResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Currency != null) {
      this.Currency = Currency.fromObject(other.Currency)
    } else {
      this.Currency = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CurrencyDeleteResponse}
   * @returns {!CurrencyDeleteResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CurrencyDeleteResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CurrencyDeleteResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CurrencyDeleteResponse}
   * @param {!CurrencyDeleteResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CurrencyDeleteResponse)) {
      throw new TypeError('Instance of CurrencyDeleteResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CurrencyDeleteResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Currency: ((this.Currency != null) ? this.Currency : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CurrencyDeleteResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CurrencyDeleteResponse} other Object value
   * @returns {!CurrencyDeleteResponse} Created struct
   */
  static fromObject (other) {
    return new CurrencyDeleteResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CurrencyDeleteResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CurrencyDeleteResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CurrencyDeleteResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6094
  }
}

export { CurrencyDeleteResponse };

/**
 * Fast Binary Encoding CurrencyDeleteResponse field model
 */
class FieldModelCurrencyDeleteResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateResponse(buffer, 4 + 4)
    this.Currency = new FieldModelCurrency(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigUpdateResponse field model
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!FieldModelConfigUpdateResponse} ConfigUpdateResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Currency.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Currency.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCurrencyDeleteResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6094
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Currency.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Currency.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Currency.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!CurrencyDeleteResponse} fbeValue Default value, defaults is new CurrencyDeleteResponse()
   * @returns {!CurrencyDeleteResponse} CurrencyDeleteResponse value
   */
  get (fbeValue = new CurrencyDeleteResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!CurrencyDeleteResponse} fbeValue CurrencyDeleteResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Currency.fbeSize) <= fbeStructSize) {
      fbeValue.Currency = this.Currency.get()
    } else {
      fbeValue.Currency = new Currency()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Currency.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCurrencyDeleteResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {!CurrencyDeleteResponse} fbeValue CurrencyDeleteResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCurrencyDeleteResponse}
   * @param {CurrencyDeleteResponse} fbeValue CurrencyDeleteResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Currency.set(fbeValue.Currency)
  }
}

export { FieldModelCurrencyDeleteResponse };

/**
 * Fast Binary Encoding CurrencyDeleteResponse model
 */
class CurrencyDeleteResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCurrencyDeleteResponse(this.buffer, 4)
  }

  /**
   * Get the CurrencyDeleteResponse model
   * @this {!CurrencyDeleteResponseModel}
   * @returns {!FieldModelCurrencyDeleteResponse} model CurrencyDeleteResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CurrencyDeleteResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CurrencyDeleteResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CurrencyDeleteResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CurrencyDeleteResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCurrencyDeleteResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CurrencyDeleteResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CurrencyDeleteResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CurrencyDeleteResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CurrencyDeleteResponseModel}
   * @param {!CurrencyDeleteResponse} value CurrencyDeleteResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CurrencyDeleteResponseModel}
   * @param {!CurrencyDeleteResponse} value CurrencyDeleteResponse value, defaults is new CurrencyDeleteResponse()
   * @return {!object} Deserialized CurrencyDeleteResponse value and its size
   */
  deserialize (value = new CurrencyDeleteResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CurrencyDeleteResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CurrencyDeleteResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CurrencyDeleteResponseModel}
   * @param {!number} prev Previous CurrencyDeleteResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CurrencyDeleteResponseModel };
